// app/src/dashboard/widgets/chart/ChartErrorBoundary.tsx
import type { ReactNode } from "react";
import AppErrorBoundary from "../../../client/components/AppErrorBoundary";
import useTranslation from "../../../client/hooks/useTranslation";

interface ChartErrorBoundaryProps {
  children: ReactNode;
}

/**
 * Local boundary around a lightweight-charts render surface.
 *
 * Defense-in-depth for the "removeChild NotFoundError" class of crash: even with
 * the inner-div isolation in useChartEngine, a residual DOM-ownership race during
 * unmount (layout edit, theme switch, fast remount) throws during React's commit
 * phase. Caught here, it stays contained to the widget — the page no longer
 * bubbles up to AppErrorBoundary and blanks entirely. The user sees a small
 * fallback with a reload affordance instead of losing the whole page.
 *
 * The error is still reported to PostHog + Sentry via AppErrorBoundary, so the
 * crash remains diagnosable (we do NOT want to silently swallow it).
 */
export default function ChartErrorBoundary({ children }: ChartErrorBoundaryProps) {
  const t = useTranslation();
  return (
    <AppErrorBoundary
      source="chart-widget"
      fallback={() => (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
          <p className="text-sm text-muted-foreground">{t.chart.unavailable}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs text-foreground hover:bg-accent"
          >
            {t.chart.reload}
          </button>
        </div>
      )}
    >
      {children}
    </AppErrorBoundary>
  );
}
